type OpenGraphConfig = {
  type?: string;
  url?: string;
  title?: string;
  description?: string;
  image?: string;
  imageAlt?: string;
  siteName?: string;
  locale?: string;
};

type TwitterConfig = {
  card?: string;
  title?: string;
  description?: string;
  image?: string;
  imageAlt?: string;
};

export type SeoConfig = {
  title: string;
  description?: string;
  robots?: string;
  canonical?: string | null;
  lang?: string;
  author?: string;
  applicationName?: string;
  openGraph?: OpenGraphConfig | null;
  twitter?: TwitterConfig | null;
  structuredData?: Record<string, unknown> | Array<Record<string, unknown>> | null;
};

const STRUCTURED_DATA_ID = 'bitbothub-seo-jsonld';

const setMetaTag = (
  selector: { name?: string; property?: string },
  content?: string | null
): void => {
  if (typeof document === 'undefined') return;

  const attributeName = selector.name ? 'name' : 'property';
  const attributeValue = selector.name ?? selector.property;
  if (!attributeValue) return;

  const existing = document.head.querySelector<HTMLMetaElement>(
    `meta[${attributeName}="${attributeValue}"]`
  );

  if (!content) {
    existing?.remove();
    return;
  }

  const meta = existing ?? document.createElement('meta');
  meta.setAttribute(attributeName, attributeValue);
  meta.setAttribute('content', content);

  if (!existing) {
    document.head.appendChild(meta);
  }
};

const setCanonicalLink = (href?: string | null): void => {
  if (typeof document === 'undefined') return;

  const existing = document.head.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]'
  );

  if (!href) {
    existing?.remove();
    return;
  }

  const link = existing ?? document.createElement('link');
  link.setAttribute('rel', 'canonical');
  link.setAttribute('href', href);

  if (!existing) {
    document.head.appendChild(link);
  }
};

const setStructuredData = (
  value?: Record<string, unknown> | Array<Record<string, unknown>> | null
): void => {
  if (typeof document === 'undefined') return;

  const existing = document.getElementById(STRUCTURED_DATA_ID);

  if (!value) {
    existing?.remove();
    return;
  }

  const script = existing ?? document.createElement('script');
  script.id = STRUCTURED_DATA_ID;
  script.setAttribute('type', 'application/ld+json');
  script.textContent = JSON.stringify(value);

  if (!existing) {
    document.head.appendChild(script);
  }
};

export const applySeo = (config: SeoConfig): void => {
  if (typeof document === 'undefined') return;

  document.title = config.title;

  if (config.lang) {
    document.documentElement.lang = config.lang;
  }

  setMetaTag({ name: 'description' }, config.description);
  setMetaTag({ name: 'robots' }, config.robots);
  setMetaTag({ name: 'author' }, config.author);
  setMetaTag({ name: 'application-name' }, config.applicationName);

  setCanonicalLink(config.canonical);

  setMetaTag({ property: 'og:type' }, config.openGraph?.type);
  setMetaTag({ property: 'og:url' }, config.openGraph?.url);
  setMetaTag({ property: 'og:title' }, config.openGraph?.title);
  setMetaTag({ property: 'og:description' }, config.openGraph?.description);
  setMetaTag({ property: 'og:image' }, config.openGraph?.image);
  setMetaTag({ property: 'og:image:alt' }, config.openGraph?.imageAlt);
  setMetaTag({ property: 'og:site_name' }, config.openGraph?.siteName);
  setMetaTag({ property: 'og:locale' }, config.openGraph?.locale);

  setMetaTag({ name: 'twitter:card' }, config.twitter?.card);
  setMetaTag({ name: 'twitter:title' }, config.twitter?.title);
  setMetaTag({ name: 'twitter:description' }, config.twitter?.description);
  setMetaTag({ name: 'twitter:image' }, config.twitter?.image);
  setMetaTag({ name: 'twitter:image:alt' }, config.twitter?.imageAlt);

  setStructuredData(config.structuredData);
};
